import { lazy, type ComponentType, type LazyExoticComponent } from "react";

import type { Permission } from "@/core/auth";

type PanelComponent = ComponentType<PanelViewProps>;
type LazyPanelComponent = LazyExoticComponent<PanelComponent>;
type PanelComponentLoader = () => Promise<{ default: PanelComponent }>;

function lazyPanel(loader: PanelComponentLoader): LazyPanelComponent {
  return lazy(loader);
}

const panelLoaders = {
  conversations: () =>
    import("@/panels/conversations").then((m) => ({ default: m.ConversationsPanel })),
  fan: () => import("@/panels/fan").then((m) => ({ default: m.FanPanel })),
  fanChat: () => import("@/panels/fan").then((m) => ({ default: m.FanChatPanel })),
  fanInfo: () => import("@/panels/fan").then((m) => ({ default: m.FanInfoPanel })),
  fanStats: () => import("@/panels/fan").then((m) => ({ default: m.FanStatsPanel })),
  fanMedia: () => import("@/panels/fan").then((m) => ({ default: m.FanMediaPanel })),
  fanTrace: () => import("@/panels/fan").then((m) => ({ default: m.FanTracePanel })),
  francis: () => import("@/panels/francis").then((m) => ({ default: m.FrancisPanel })),
  analytique: () =>
    import("@/panels/analytique").then((m) => ({ default: m.AnalytiquePanel })),
  organisation: () =>
    import("@/panels/organisation").then((m) => ({ default: m.OrganisationPanel })),
  organisationDetail: () =>
    import("@/panels/organisation").then((m) => ({ default: m.OrganisationDetailPanel })),
  personnel: () => import("@/panels/personnel").then((m) => ({ default: m.PersonnelPanel })),
  affilie: () =>
    import("@/panels/affilie").then((m) => ({ default: m.AffiliePanel })),
  rishfans: () =>
    import("@/panels/rishfans").then((m) => ({ default: m.RishfansPanel })),
  documentation: () =>
    import("@/panels/documentation").then((m) => ({ default: m.DocumentationPanel })),
  formation: () =>
    import("@/panels/formation").then((m) => ({ default: m.FormationPanel })),
  sites: () => import("@/panels/sites").then((m) => ({ default: m.SitesPanel })),
  telegram: () =>
    import("@/channels/telegram/TelegramPanel").then((m) => ({ default: m.TelegramPanel })),
  twitter: () =>
    import("@/channels/twitter/TwitterPanel").then((m) => ({ default: m.TwitterPanel })),
  "twitter-sites": () =>
    import("@/channels/twitter/TwitterSitesPanel").then((m) => ({
      default: m.TwitterSitesPanel,
    })),
  "telegram-sites": () =>
    import("@/channels/telegram/TelegramSitesPanel").then((m) => ({
      default: m.TelegramSitesPanel,
    })),
  "canaux-telegram": () =>
    import("@/channels/telegram/canaux/CanauxPanel").then((m) => ({
      default: m.CanauxPanel,
    })),
  spaceDetail: () =>
    import("@/panels/spaces").then((m) => ({ default: m.SpaceDetailPanel })),
  vaultItem: () =>
    import("@/panels/vault-item").then((m) => ({ default: m.VaultItemPanel })),
  vaultReview: () =>
    import("@/panels/vault-review").then((m) => ({ default: m.VaultReviewPanel })),
  empty: () => import("@/panels/empty").then((m) => ({ default: m.EmptyPanel })),
} satisfies Record<string, PanelComponentLoader>;

export function preloadPanel(panelId: PanelId): void {
  const loaderByPanel: Partial<Record<PanelId, PanelComponentLoader>> = {
    conversations: panelLoaders.conversations,
    fan: panelLoaders.fan,
    "fan-chat": panelLoaders.fanChat,
    "fan-info": panelLoaders.fanInfo,
    "fan-stats": panelLoaders.fanStats,
    "fan-media": panelLoaders.fanMedia,
    "fan-trace": panelLoaders.fanTrace,
    francis: panelLoaders.francis,
  };
  void loaderByPanel[panelId]?.();
}

const ConversationsPanel = lazyPanel(panelLoaders.conversations);
const FanPanel = lazyPanel(panelLoaders.fan);
const FanChatPanel = lazyPanel(panelLoaders.fanChat);
const FanInfoPanel = lazyPanel(panelLoaders.fanInfo);
const FanStatsPanel = lazyPanel(panelLoaders.fanStats);
const FanMediaPanel = lazyPanel(panelLoaders.fanMedia);
const FanTracePanel = lazyPanel(panelLoaders.fanTrace);
const FrancisPanel = lazyPanel(panelLoaders.francis);
const AnalytiquePanel = lazyPanel(panelLoaders.analytique);
const OrganisationPanel = lazyPanel(panelLoaders.organisation);
const OrganisationDetailPanel = lazyPanel(panelLoaders.organisationDetail);
const PersonnelPanel = lazyPanel(panelLoaders.personnel);
const AffiliePanel = lazyPanel(panelLoaders.affilie);
const RishfansPanel = lazyPanel(panelLoaders.rishfans);
const DocumentationPanel = lazyPanel(panelLoaders.documentation);
const FormationPanel = lazyPanel(panelLoaders.formation);
const SitesPanel = lazyPanel(panelLoaders.sites);
const TelegramPanel = lazyPanel(panelLoaders.telegram);
const TwitterPanel = lazyPanel(panelLoaders.twitter);
const TwitterSitesPanel = lazyPanel(panelLoaders["twitter-sites"]);
const TelegramSitesPanel = lazyPanel(panelLoaders["telegram-sites"]);
const CanauxPanel = lazyPanel(panelLoaders["canaux-telegram"]);
const SpaceDetailPanel = lazyPanel(panelLoaders.spaceDetail);
const VaultItemPanel = lazyPanel(panelLoaders.vaultItem);
const VaultReviewPanel = lazyPanel(panelLoaders.vaultReview);
const EmptyPanel = lazyPanel(panelLoaders.empty);
// Panneaux de canal — import en chemin profond (PAS via le barrel `@/channels`)
// pour éviter un cycle : `creatorTabs` importe `@/channels`, donc faire passer
// ces panneaux par le barrel boucle (barrel → TelegramPanel → creatorTabs →
// barrel).

/**
 * Props injectees a TOUTE view component d'un panel.
 *
 * Convention : chaque component de panel accepte ces props (toutes optionnelles
 * sauf instanceId qui peut servir pour des actions in-panel — ex: pin, close).
 */
export interface PanelViewProps {
  /** ID unique de l'instance dans le layout (pour cross-references). */
  instanceId?: string;
  /** Parametres specifiques au panel (data scope, filtres). */
  params?: Record<string, unknown>;
}

/**
 * Stable IDs of all registered panels. Adding a new panel requires extending
 * this union AND adding an entry to `panelRegistry` below.
 */
export type PanelId =
  | "empty"
  | "conversations"
  | "fan"
  | "fan-chat"
  | "fan-info"
  | "fan-stats"
  | "fan-media"
  | "fan-trace"
  // Ouverts par un clic dans le Vault et dans Spaces — deux onglets que
  // Telegram et Twitter embarquent, donc encore atteints par les kiosques
  // manager et collaborateur.
  | "space-detail"
  | "vault-item"
  | "vault-review"
  // 🤖 Francis — la colonne de l'agent, à droite du poste de chatteur. Pas
  // branché : la place est prise (Kayla, 10-09-2026), le reste vient.
  | "francis"
  // Analytique — l'écran de la fondatrice (09-10-2026) : l'argent de la
  // semaine (lundi → dimanche) et du mois, ce qui reste dans sa poche, les
  // associés, l'acquisition, les pays. Gate team.manage_compensation (CEO) :
  // il montre ce que touche chacun.
  | "analytique"
  | "organisation"
  | "organisation-detail"
  | "personnel"
  // Mon lien — la SEULE page d'un affilié (cf `KIOSK_TABS.affilie`).
  // Sans `requiredPermission` : son rôle n'en porte aucune, et le scope
  // vit côté serveur (`affilie_workos_user_id`).
  | "affilie"
  // RishFans — la SEULE page d'un partenaire RishFans (cf
  // `KIOSK_TABS.partenaire_rishfans`) : ses chiffres, ses liens d'invitation,
  // son portefeuille. Sans `requiredPermission` : son rôle n'en porte aucune,
  // le scope vit côté serveur (`/api/partenaire/*`).
  | "rishfans"
  // Documentation — la fiche « L'art de la confiance » (recherche du 11-09),
  // servie telle quelle depuis `public/doc/`. Depuis le 12-09 (Kayla) c'est
  // TOUTE la doc du chatteur : les sept onglets d'avant (menu, contenu, faire
  // payer, sorties, jeu, rencontre) sont partis. Vit à côté de Personnel dans
  // le kiosk chatter. Accessible à tout membre.
  | "documentation"
  // Formation — les vidéos de Kayla, dans l'ordre (12-09-2026). Tout membre
  // les regarde ; qui tient la grille des shifts les ajoute et les réordonne.
  // Deuxième onglet du kiosk chatter : c'est ce qu'un chatteur neuf voit
  // avant son premier shift.
  | "formation"
  | "sites"
  // 🪦 Le Lab, les Métriques, Cyrano (le pilotage), Herald, Créatrices, le
  // Vault autonome, l'Audio, Spaces autonome, Sécurité, Sans
  // réponse, Relances et l'alias `team` sont sortis du registre le 04-11-2026
  // (Kayla : « mon cockpit DOZKA, c'est un dépotoir »). Un workspace persisté
  // qui les porte encore retombe sur `empty`, comme `explorer` plus bas.
  // Panneaux de canal (top-level) — connexion/groupes/commandes/flow par canal.
  | "telegram"
  | "telegram-sites"
  | "canaux-telegram"
  | "twitter"
  | "twitter-sites";
  // 🪦 `explorer`, `dozstore`, `app-page`, `app-frame` ont vécu ici jusqu'au
  // 08-10-2026 (Kayla : « supprimer le délire de l'accueil, comme dans
  // Windows »). Un workspace persisté qui les porte encore retombe sur `empty`.

/**
 * Groupes de panels — utilises pour l'affichage par sections dans le
 * panel picker. Un panel sans `group` declare est implicitement dans
 * `"Default"`. Les panels `hidden: true` ne sont jamais affiches.
 *
 * Ajouter un groupe : suffit de declarer un nouveau libelle dans le champ
 * `group` d'une PanelDefinition. L'ordre d'apparition dans le picker suit
 * l'ordre de premiere apparition dans `panelRegistry`.
 */
export type PanelGroup = "Default" | "Admin" | "Channels";

/**
 * Une "vue" d'un panel — un meme panel peut avoir plusieurs vues
 * (ex: panel "Chats" avec vues "Liste" / "Grille" / "Analytics").
 *
 * NB : "view" ici = mode d'affichage interne au panel ; ne pas confondre
 * avec "espace de travail" (workspace, terminologie Phase 4) qui est un
 * preset de layout global.
 */
export interface PanelView {
  /** Stable view ID — utilise dans `PanelInstance.viewId` et la persistence. */
  id: string;
  /** Nom affiche dans le selecteur d'affichage. */
  name: string;
  /** Composant React rendu quand cette vue est active. */
  component: PanelComponent | LazyPanelComponent;
  /**
   * Hint optionnel : aspect ratio prefere du conteneur. Le shell peut
   * suggerer cette vue selon la forme du slot ou utiliser cet hint pour
   * l'auto-pick au drop.
   */
  preferredAspect?: "vertical" | "horizontal" | "square";
}

export interface PanelDefinition {
  /** Unique panel ID — utilise dans URL state + workspace persistence. */
  id: PanelId;
  /** Titre humain affiche dans l'onglet. */
  title: string;
  /** Description courte pour le panel picker / workspace editor. */
  description: string;
  /** N >= 1 vues. Si N === 1, pas de view picker affiche dans le header. */
  views: PanelView[];
  /** ID de la vue par defaut (doit exister dans `views`). */
  defaultViewId: string;
  /** Lucide icon name — optional. */
  icon?: string;
  /** Tags pour la recherche / le filtrage de panels. */
  tags?: string[];
  /**
   * Section sous laquelle ce panel apparait dans le picker.
   * Defaut implicite = "Default".
   */
  group?: PanelGroup;
  /** Tailles minimum du conteneur (px). Phase 5 wiring. */
  minWidth?: number;
  minHeight?: number;
  /**
   * Cache du PanelPicker (Add a tab) et du LayoutPicker. Mettre `true` pour
   * les panels qui ne doivent JAMAIS etre crees manuellement par l'user —
   * typiquement ceux qui sont ouverts par le code en reponse a une action
   * (ex: `fan` est ouvert par un click dans Conversations, jamais via le
   * picker, car il a besoin de params specifiques pour fonctionner).
   */
  hidden?: boolean;
  /**
   * Permission RBAC requise pour voir/ouvrir ce panel. Si absente, le panel
   * est accessible à tous les membres. Le PanelPicker filtre les panels
   * dont l'user n'a pas la permission ; PanelHost rend un fallback "Access
   * denied" si un workspace persisté contient un panel hors permissions.
   *
   * Convention : `<panel>.view` pour la consultation (le minimum requis
   * pour rendre le panel). Les actions plus fines (`.update`, `.create`)
   * sont gérées au cas par cas par les composants enfants.
   */
  requiredPermission?: Permission;
  /**
   * Capability exigée pour que ce panel soit PROPOSÉ dans le PanelPicker, EN
   * PLUS de `requiredPermission`. Même source que le rail du Bureau :
   * `useCapabilities()` (cf `apps/useCapabilities.ts`). Vaut la capability du
   * canal dont ce panneau est l'écran.
   *
   * 🪤 Ici vivait `requiresChannelAccess?: boolean`, que le picker résolvait
   * avec `useCanUseChannels` — l'e-mail du propriétaire EN DUR — pendant que
   * l'Explorateur (parti le 08-10-2026) passait par les capabilities. Un membre à qui l'accès
   * Twitter était ACCORDÉ trouvait donc l'app d'un côté et pas de l'autre.
   * Deux sources pour une même question finissent par diverger : il n'y en a
   * plus qu'une, et `panelVisibility.test.ts` casse si le panneau d'une app
   * gardée oublie de la déclarer.
   *
   * Gate FRONT — le backend ré-impose le contrôle.
   */
  requiredCapability?: string;
}

/**
 * Single source of truth des panels disponibles dans le cockpit.
 * Order is irrelevant — workspaces pick whichever panels they need by ID.
 */
export const panelRegistry: Record<PanelId, PanelDefinition> = {
  conversations: {
    id: "conversations",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Conversations",
    description:
      "All conversations from all creators in a responsive grid",
    views: [
      { id: "default", name: "Default", component: ConversationsPanel },
    ],
    defaultViewId: "default",
    tags: ["conversations", "prod"],
    group: "Default",
    requiredPermission: "conversations.view",
  },
  fan: {
    id: "fan",
    title: "Fan",
    description: "Detail view for a single fan (chat history + stats)",
    views: [
      { id: "default", name: "Default", component: FanPanel },
    ],
    defaultViewId: "default",
    tags: ["fan", "detail"],
    // Toujours ouvert par un click dans Conversations (jamais via picker) —
    // un Fan sans `params.conversationId` n'a pas de sens.
    hidden: true,
    requiredPermission: "conversations.view",
  },
  // ─── Sub-vues detachees du FanPanel ──────────────────────────────────
  // Ouvertes par drag d'un sub-tab du SubNav vers un edge/group du layout.
  // Suivent l'active conversation du fan group d'ou elles ont ete drag
  // (params.sourceFanGroupId). Cf @/panels/fan/FanSubViewPanel.tsx.
  "fan-chat": {
    id: "fan-chat",
    title: "Fan chat",
    description: "Chat view of the active fan in the source Fan group",
    views: [{ id: "default", name: "Default", component: FanChatPanel }],
    defaultViewId: "default",
    tags: ["fan", "chat", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  "fan-info": {
    id: "fan-info",
    title: "Fan info",
    description: "Info view of the active fan in the source Fan group",
    views: [{ id: "default", name: "Default", component: FanInfoPanel }],
    defaultViewId: "default",
    tags: ["fan", "info", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  francis: {
    id: "francis",
    title: "Francis",
    description: "L'agent du chatteur, à droite du poste — pas encore branché",
    views: [{ id: "default", name: "Default", component: FrancisPanel }],
    defaultViewId: "default",
    tags: ["fan", "agent", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  "fan-stats": {
    id: "fan-stats",
    title: "Fan stats",
    description: "Stats view of the active fan in the source Fan group",
    views: [{ id: "default", name: "Default", component: FanStatsPanel }],
    defaultViewId: "default",
    tags: ["fan", "stats", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  "fan-media": {
    id: "fan-media",
    title: "Fan media",
    description: "Media view of the active fan in the source Fan group",
    views: [{ id: "default", name: "Default", component: FanMediaPanel }],
    defaultViewId: "default",
    tags: ["fan", "media", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  "fan-trace": {
    id: "fan-trace",
    title: "Fan observation",
    description: "Turn-by-turn pipeline observation of the active fan",
    views: [{ id: "default", name: "Default", component: FanTracePanel }],
    defaultViewId: "default",
    tags: ["fan", "trace", "detached"],
    hidden: true,
    requiredPermission: "conversations.view",
  },
  analytique: {
    id: "analytique",
    title: "Analytique",
    description:
      "L'argent de la semaine et du mois, ce qui reste dans ma poche, les associés et les comptes, l'acquisition (joins, DM, langues) et les pays.",
    views: [{ id: "default", name: "Default", component: AnalytiquePanel }],
    defaultViewId: "default",
    tags: ["analytique", "analytics", "argent", "revenu", "associés", "pays", "acquisition", "dashboard"],
    // La fondatrice seule : l'écran dit ce que touche chaque membre et
    // associé. La route backend `/api/analytique` exige le rôle `ceo` aussi.
    requiredPermission: "team.manage_compensation",
    group: "Admin",
  },
  organisation: {
    id: "organisation",
    title: "Organisation",
    description:
      "Browse the organisations you belong to — open one to see its creators, team members, payments and stats.",
    views: [
      { id: "default", name: "Default", component: OrganisationPanel },
    ],
    defaultViewId: "default",
    tags: ["organisation", "team", "admin", "members", "creators"],
    group: "Admin",
    // Réservé aux rôles « habilités » à gérer l'org : Manager + CEO. Gate sur
    // `team.invite` (Manager/CEO uniquement — pas Operator/VA/Chatter). Les
    // gates fines (manage compensation, etc.) restent appliquées in-panel sur
    // le tab Team du OrganisationDetailPanel. Voir le commentaire jumeau dans
    // src/apps/registry.ts (app `organisation`).
    requiredPermission: "team.invite",
  },
  sites: {
    id: "sites",
    // 🧭 Un onglet de Telegram depuis le 08-10-2026 (cf `ecransDuBureau.ts`) :
    // plus dans le picker « Ajouter un panneau ». Le panneau reste
    // enregistré pour les workspaces persistés et les kiosques (`KIOSK_TABS`).
    hidden: true,
    title: "Sites",
    description:
      "Les sites vitrines de l'organisation — trafic en direct, pages les plus vues, sources, pays, et le catalogue publie (modeles, categories) croise avec les vues reelles.",
    views: [{ id: "default", name: "Default", component: SitesPanel }],
    defaultViewId: "default",
    tags: [
      "sites",
      "site",
      "cumgram",
      "trafic",
      "audience",
      "visiteurs",
      "seo",
      "umami",
      "analytics",
      "catalogue",
    ],
    group: "Admin",
    // Le perimetre SITE, et plus `team.invite` (27-08-2026). Le gate n'a pas
    // change d'audience — CEO et Manager portent les deux — mais il se donne
    // desormais SANS le droit d'inviter, ce qu'un collaborateur externe ne
    // doit pas avoir. Cf `sites.view` dans convex/_lib/permissions.ts.
    requiredPermission: "sites.view",
  },
  "organisation-detail": {
    id: "organisation-detail",
    title: "Organisation",
    description:
      "Detail view for a single organisation — logo, members, creators, revenue + tab bar with one tab per creator and a final Team tab. Opened by clicking a card in the Organisation panel.",
    views: [
      { id: "default", name: "Default", component: OrganisationDetailPanel },
    ],
    defaultViewId: "default",
    tags: ["organisation", "detail"],
    // Toujours ouvert par un click dans Organisation (jamais via picker) —
    // un organisation-detail sans params.accountId n'a pas de sens.
    hidden: true,
    // Même gate que le panel Organisation parent (Manager + CEO).
    requiredPermission: "team.invite",
  },
  personnel: {
    id: "personnel",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Personnel",
    description:
      "Ton espace perso — tableau de bord, analytics et portefeuille (solde en attente, paiements reçus, factures PDF).",
    views: [{ id: "default", name: "Default", component: PersonnelPanel }],
    defaultViewId: "default",
    tags: [
      "personnel",
      "perso",
      "moi",
      "me",
      "wallet",
      "portefeuille",
      "paiements",
      "stats",
      "dashboard",
      "analytics",
    ],
    group: "Default",
    // Pas de requiredPermission : tout membre a droit à son espace perso
    // (ses propres stats + son portefeuille, scopés serveur sur sa session).
  },
  affilie: {
    id: "affilie",
    title: "Mon lien",
    description:
      "Ce que ton lien a rapporté — arrivées, venus en DM, ventes, brut, et les gens que tu as amenés.",
    views: [{ id: "default", name: "Default", component: AffiliePanel }],
    defaultViewId: "default",
    tags: ["affilie", "affiliation", "lien", "pub", "stats", "mon lien"],
    group: "Default",
    // 🪤 HORS du sélecteur de panneaux, et le banc l'a exigé : sans ce
    // drapeau, « Mon lien » se proposait à TOUT LE MONDE (le partenaire l'a
    // vu apparaître dans sa liste). Ce n'est pas un panneau qu'on choisit,
    // c'est la page d'un rôle — le kiosque l'injecte par son id, ce que
    // `hidden` n'empêche pas.
    hidden: true,
    // ⛔ Pas de requiredPermission, et ce n'est pas un oubli : le rôle Affilié
    // n'en porte AUCUNE (cf `BUILTIN_ROLES.affilie`), donc en exiger une le
    // mettrait dehors de sa propre page. Le scope est SERVEUR —
    // `GET /api/affilies/me` filtre sur `affilie_workos_user_id`, jamais sur un
    // rôle : quelqu'un qui ouvrirait ce panneau sans être affilié n'y verrait
    // rien, parce que le backend ne lui rend rien.
  },
  rishfans: {
    id: "rishfans",
    title: "RishFans",
    description:
      "Ton espace partenaire — tes liens d'invitation, ce qu'ils rapportent (arrivées, passés en DM, ventes, CA) et ton portefeuille.",
    views: [{ id: "default", name: "Default", component: RishfansPanel }],
    defaultViewId: "default",
    tags: ["rishfans", "partenaire", "liens", "invitation", "stats", "portefeuille"],
    group: "Default",
    // 🪤 HORS du sélecteur de panneaux, comme « Mon lien » : ce n'est pas un
    // panneau qu'on choisit, c'est la page d'un rôle — le kiosque l'injecte par
    // son id, ce que `hidden` n'empêche pas.
    hidden: true,
    // ⛔ Pas de requiredPermission, et ce n'est pas un oubli : le rôle
    // `partenaire_rishfans` n'en porte AUCUNE (cf `BUILTIN_ROLES`), donc en
    // exiger une le mettrait dehors de sa propre page. Le scope est SERVEUR —
    // `/api/partenaire/*` filtre sur l'espace de la session, jamais sur un
    // rôle : quelqu'un qui ouvrirait ce panneau sans espace recevrait 403 et
    // verrait « pas encore activé ».
  },
  documentation: {
    id: "documentation",
    // 🧭 Hors du rail et du picker depuis le 08-10-2026 (cf `ecransDuBureau.ts`).
    // Kayla, 08-10-2026 : « pourquoi j'ai une application de documentation alors que je ne suis pas un chatteur » — reste l'onglet du kiosque chatteur.
    hidden: true,
    title: "Documentation",
    description:
      "Le guide du chatteur — reprendre la main quand Cyrano a posé l'ultimatum, le menu et ses six offres, quoi envoyer, faire payer, le jeu, la rencontre. Visuel, simple, avec exemples.",
    views: [{ id: "default", name: "Default", component: DocumentationPanel }],
    defaultViewId: "default",
    tags: [
      "documentation",
      "doc",
      "aide",
      "guide",
      "règles",
      "regles",
      "chatteur",
      "menu",
      "prix",
      "vente",
      "jeu",
    ],
    group: "Default",
    // Pas de requiredPermission : c'est le guide du chatteur, accessible à tout
    // membre (troisième onglet du kiosk chatter). Rien de sensible.
  },
  formation: {
    id: "formation",
    // 🧭 Un onglet de l'Organisation depuis le 08-10-2026 (cf `ecransDuBureau.ts`) :
    // plus dans le picker « Ajouter un panneau ». Le panneau reste
    // enregistré pour les workspaces persistés et les kiosques (`KIOSK_TABS`).
    hidden: true,
    title: "Formation",
    description:
      "Les vidéos de formation des chatteurs, dans l'ordre. Le CEO et le manager les ajoutent, les réordonnent, les retirent — sans déploiement.",
    views: [{ id: "default", name: "Default", component: FormationPanel }],
    defaultViewId: "default",
    tags: ["formation", "vidéo", "video", "apprendre", "onboarding", "chatteur"],
    group: "Default",
    // Pas de requiredPermission : tout membre regarde. L'édition est gardée
    // côté Convex (`team.manage_schedules`) et cachée sans elle.
  },
  telegram: {
    id: "telegram",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Telegram",
    description:
      "Manage a creator's Telegram channel — bot connection, groups, slash commands and conversational flows. Pick a creator, then navigate Connection / Group / Command / Flow.",
    views: [{ id: "default", name: "Default", component: TelegramPanel }],
    defaultViewId: "default",
    tags: ["telegram", "channel", "connection", "groups", "commands", "flow", "bot"],
    group: "Channels",
    // Même périmètre que le détail créateur : ces surfaces y vivaient avant.
    requiredPermission: "creators.view",
  },
  "telegram-sites": {
    id: "telegram-sites",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Telegram — Sites",
    description:
      "Le canal Telegram des SITES (cumgram.com et les suivants) : le bot, ses groupes, ses commandes, l'accueil, les liens d'invitation, les diffusions et les flows. Parc séparé de celui des créatrices — les deux ne se croisent dans aucun écran.",
    views: [{ id: "default", name: "Default", component: TelegramSitesPanel }],
    defaultViewId: "default",
    tags: [
      "telegram",
      "site",
      "sites",
      "cumgram",
      "bot",
      "automatisation",
      "flow",
      "accueil",
      "diffusion",
    ],
    group: "Channels",
    // Le périmètre SITE, comme `sites` et `twitter-sites` — et surtout PAS
    // `creators.view`, qui ouvrirait du même geste le canal Telegram des
    // créatrices au collaborateur externe.
    requiredPermission: "sites.view",
    // ⛔ Sa PROPRE capability, jamais `telegram`. Celle-ci est dans
    // `BASE_CAPABILITIES` (toute org l'a) : s'y adosser rendrait cette app
    // visible de tout le monde, et surtout inaccordable séparément. Même
    // raison que `twitter-sites` vs `twitter`.
    requiredCapability: "telegram-sites",
  },
  // Les canaux Telegram rangés comme les comptes X (Kayla, 30-09-2026) :
  // famille × langue, leurs chiffres, un lien par compte X. Pas de capability :
  // c'est le BACKEND qui borne ce qu'on voit — tous les canaux de ses
  // créatrices, plus ceux dont on est le partenaire (Marko : Dark Trista).
  // `creators.view` est ce que le collaborateur porte déjà.
  "canaux-telegram": {
    id: "canaux-telegram",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Canaux",
    description:
      "Les canaux Telegram rangés par famille (Fav, Dark) et par langue, leurs chiffres côte à côte, et un lien d'invitation par compte X — toujours en demande d'adhésion.",
    views: [{ id: "default", name: "Default", component: CanauxPanel }],
    defaultViewId: "default",
    tags: ["telegram", "canaux", "liens", "invitation", "fav", "dark", "langue"],
    group: "Channels",
    requiredPermission: "creators.view",
  },
  twitter: {
    id: "twitter",
    // 🧹 Hors du rail et du picker de DOZKA depuis le 04-11-2026 : il ne sert
    // plus qu'aux kiosques (`KIOSK_TABS`), qui l'injectent par son id.
    hidden: true,
    title: "Twitter",
    description:
      "Manage a creator's Twitter channel — X accounts and proxies (Dozka admin only). Pick a creator, then manage its Connection (accounts + proxies).",
    views: [{ id: "default", name: "Default", component: TwitterPanel }],
    defaultViewId: "default",
    tags: ["twitter", "x", "channel", "accounts", "proxies"],
    group: "Channels",
    // Caché du picker pour qui n'a pas la capability `twitter` (propriétaire OU
    // accès accordé dans `appAccess`, cf `useCapabilities`) — sinon ils voyaient
    // l'entrée puis le mur « réservé ». Gate ré-imposé dans TwitterPanel + backend.
    requiredPermission: "creators.view",
    requiredCapability: "twitter",
  },
  "twitter-sites": {
    id: "twitter-sites",
    // 🧭 Plus dans le picker depuis le 08-10-2026 : chez Kayla, les sites sont
    // des SOURCES de l'app Twitter (Cyrano · Helios · un par site, cf
    // `sources.ts`). Le panneau reste le kiosque du partenaire (`KIOSK_TABS`).
    hidden: true,
    title: "Twitter — Sites",
    description:
      "Le canal X des SITES (cumgram.com et les suivants) : leurs comptes scrapeurs et posteurs, leurs proxys, ce qu'ils publient et la bibliothèque qu'ils récoltent. Parc séparé de celui des créatrices — les deux ne se croisent dans aucun écran.",
    views: [{ id: "default", name: "Default", component: TwitterSitesPanel }],
    defaultViewId: "default",
    tags: ["twitter", "x", "site", "sites", "cumgram", "scrapeur", "posteur", "proxies"],
    group: "Channels",
    // 🩸 Ce panneau exigeait `requiredCapability: "twitter"` — la capability de
    // l'app des CRÉATRICES. Accorder l'une donnait donc l'autre, et il n'existait
    // aucun moyen de n'accorder que les sites : exactement ce que cette deuxième
    // app existe pour empêcher (« sinon ça va être trop mélangé », Kayla,
    // 27-08-2026). Sa capability est maintenant la sienne.
    //
    // ⚠️ Séparer les deux parcs reste un rangement d'écrans, JAMAIS une ACL : ce
    // qu'un membre a le droit de voir se joue dans `memberCreatorScopes`
    // (Convex) → `member_creator_scopes` (Neon) → `visible_creator_ids`, et
    // nulle part ici.
    // 🩸 `creators.view` a garde ce panneau jusqu'au 27-08-2026. Elle le
    // laissait passer, mais elle ouvrait DU MEME GESTE le panneau Creators et
    // Telegram : impossible de donner l'app des sites sans donner celles des
    // creatrices. `sites.view` ne garde que le perimetre site.
    requiredPermission: "sites.view",
    requiredCapability: "twitter-sites",
  },
  "space-detail": {
    id: "space-detail",
    title: "Space",
    description:
      "Detail view of a single Telegram Space — messages, members, info. Opened by clicking a card in the Spaces panel.",
    views: [
      { id: "default", name: "Default", component: SpaceDetailPanel },
    ],
    defaultViewId: "default",
    tags: ["spaces", "telegram", "detail", "messages"],
    // Toujours ouvert par un click dans Spaces (jamais via picker) —
    // un space-detail sans params.spaceId n'a pas de sens.
    hidden: true,
    // Même gate que le panel Spaces parent.
    requiredPermission: "spaces.view",
  },  "vault-item": {
    id: "vault-item",
    title: "Vault item",
    description: "Detail + enrichment editor for a single vault item",
    views: [
      { id: "default", name: "Default", component: VaultItemPanel },
    ],
    defaultViewId: "default",
    tags: ["vault", "item", "enrich"],
    // Toujours ouvert par un click dans Vault (jamais via picker) —
    // un vault-item sans `params.itemId` n'a pas de sens.
    hidden: true,
    requiredPermission: "vault.view",
  },  "vault-review": {
    id: "vault-review",
    // 🧭 Un onglet de Créatrices depuis le 08-10-2026 (cf `ecransDuBureau.ts`) :
    // plus dans le picker « Ajouter un panneau ». Le panneau reste
    // enregistré pour les workspaces persistés et les kiosques (`KIOSK_TABS`).
    hidden: true,
    title: "Vault Review",
    description:
      "Queue of AI-analyzed media pending validation — correct the fields, pick a folder, validate before they become visible to the AI agents.",
    views: [{ id: "default", name: "Default", component: VaultReviewPanel }],
    defaultViewId: "default",
    tags: ["vault", "review", "validation", "grand-vault", "regie"],
    group: "Default",
    // Même gate que les autres écrans de curation Vault (édition des items) —
    // `vault.view` suffirait à consulter, mais valider MODIFIE les items donc
    // on gate sur `vault.update` (existe déjà, cf `usePermissions.ts`).
    requiredPermission: "vault.update",
  },  empty: {
    id: "empty",
    title: "Panel",
    description: "Empty slot — placeholder for a future panel",
    views: [{ id: "default", name: "Default", component: EmptyPanel }],
    defaultViewId: "default",
    tags: ["empty"],
    // Cache du picker (Ctrl+Shift+P + mini-picker "+"). Reste enregistre
    // car PanelHost fallback dessus quand un panel id inconnu est rencontre
    // (cf @/shell/render/PanelHost.tsx).
    hidden: true,
  },
};

export const panelList: PanelDefinition[] = Object.values(panelRegistry);

/**
 * Resolve la PanelView active pour une instance.
 * Fallback : viewId demande → defaultViewId → premiere view de la liste.
 */
export function resolvePanelView(
  def: PanelDefinition,
  viewId: string | undefined
): PanelView {
  if (viewId) {
    const explicit = def.views.find((v) => v.id === viewId);
    if (explicit) return explicit;
  }
  const defaultView = def.views.find((v) => v.id === def.defaultViewId);
  if (defaultView) return defaultView;
  // Should not happen if registry is well-formed, but be defensive
  return def.views[0]!;
}
